/**
 * **Whose navy is at sea on this map** — the rule that decides which ensign an
 * escort flies, and the supersession of record for the refusal `ship-sim.md`
 * §2.4 shipped with.
 *
 * ## What was refused, and why the refusal was right
 *
 * The first cut of ENHANCED SIMULATION put every ship under the **White
 * Ensign**, and said so at length: `mission-world.ts`'s `DEFAULT_FRONT_LINE` is
 * a placeholder at `zM = 0`, and the last thing in this port that took it
 * literally put **434 of 434** sampled flak-belt positions in the North Sea
 * (`london-sea-placements.md` §3.2). Measured against Dunkirk's own water mask
 * the same coordinate calls all but one of that map's wet sample points
 * enemy-held. A *Kaiserliche Marine* sloop placed on the strength of that
 * number would be a picture built on a coordinate the port already knows is
 * unverified, so `ENSIGN_CENTRAL` flew nowhere a generator could reach.
 *
 * ## What replaces it, and why it is not the same mistake
 *
 * **This rule never asks where the front is. It asks whether the region has
 * one** — which is a different question, answered by different evidence, and
 * answered already.
 *
 * `mission-world.ts`'s `REGION_HAS_FRONT_LINE` is a **per-region fact recovered
 * from the game's own shipped data**: `MAP1.DAT` is the one briefing map of the
 * five with no front polyline on it (world-terrain.md §2.5, re-parsed for
 * `london-sea-placements.md` §1.3), and `map/map-model.ts` already refuses to
 * draw a trench across Kent. It is a boolean, not a coordinate, so it cannot be
 * "in the sea" and it cannot be off by sixty kilometres. It is the same datum
 * `flak-placement.ts` already turns into a *side* decision — and that is the
 * precedent this file follows rather than invents:
 *
 * | map | `frontLineFlak` / `homeDefenceFlak` | this file |
 * |---|---|---|
 * | has a front | **both sides'** batteries, nine a side, because the original places nine a side | **both navies**, drawn per ship |
 * | has none (London) | **one side's**, and it is the defender's | **one navy**, and it is the defender's |
 *
 * So the ships inherit the engine's existing answer to *"how does this port
 * decide which side of a map is whose"* — which is that on a trench sector both
 * armies are present and on a home-defence map only the defender is. Nothing
 * about where.
 *
 * ## The one thing that is declared rather than recovered
 *
 * Within a contested sector, **which** navy a given escort belongs to is a
 * seeded draw, and `SHIP_CENTRAL_SHARE` is ours. The history is real and is the
 * reason a mixed draw is the honest shape at all: the Flanders Flotilla worked
 * out of Zeebrugge and Ostend for the whole war and fought the Dover Patrol
 * across the same water, so the Channel off Dunkirk was contested rather than
 * Allied. It was not *evenly* contested — the Dover Patrol outnumbered the
 * Flanders Flotilla throughout — which is what the share says and why it is not
 * a half.
 *
 * **London stays entente whatever the seed**, and that is the same sentence
 * `homeDefenceFlak` writes about its guns: there was no German squadron in the
 * Thames Estuary, and a home-defence map has a defender rather than two sides.
 *
 * ## Bit-zero, and the census
 *
 * The draw is a **private `RngBank`** salted for the ensign alone, exactly as
 * `flak-placement.ts` and `ship-deployment.ts` are, so it cannot move a draw any
 * other generator makes. And it runs **after** `deployShips` has returned, over
 * the list it produced, touching only `side` and `alliance` — so the patrols
 * themselves are bit-for-bit what they were, and
 * `ship-deployment-census.test.ts`'s 118 ships and 25 808 wet points are
 * untouched by construction rather than by re-measurement. (They were
 * re-measured anyway.)
 */

import { fnv1a32 } from "../flight/hash.ts";
import type { EncounterSide } from "../combat/encounter-sides.ts";
import { createRngBankState, RngBank } from "../flight/rng.ts";
import {
  deployShips,
  type ShipAlliance,
  type ShipSpec,
  type WaterAt,
} from "../combat/ship-deployment.ts";
import type { Nation } from "./mission-roster.ts";
import { regionHasFrontLine, type MissionRegion } from "./mission-world.ts";

/**
 * The share of escorts in a contested sector that fly the Reichskriegsflagge.
 * **OURS** — see the module note. A third: the coast is contested, and it is
 * contested by an inferior force.
 */
export const SHIP_CENTRAL_SHARE = 1 / 3;

export interface MissionShipContext {
  /** The mission's own id, for the salt — so two catalog entries on one seed do not draw identically. */
  readonly id: string;
  readonly seed: number;
  readonly region: MissionRegion;
  readonly playerNation: Nation;
  readonly isWater: WaterAt;
}

/**
 * Whether a ship of this alliance is on the player's side.
 *
 * *Friendly* is a fact about the player and *entente* is a fact about the ship,
 * which is precisely why the model carries two ensign nodes named for the
 * alliances rather than one named `ENSIGN_FRIENDLY` (`ship-design.md` §5).
 */
export function shipSideFor(alliance: ShipAlliance, nation: Nation): EncounterSide {
  const playerIsEntente = nation === "allied";
  return (alliance === "entente") === playerIsEntente ? "friendly" : "hostile";
}

/**
 * The escorts a sortie puts to sea, with the flag each one flies and the side
 * that follows from it.
 *
 * Deployment first and flags second, which is the ordering that keeps the
 * census honest: `deployShips` is called with exactly the arguments it was
 * called with before this file existed, so every patrol it draws is the patrol
 * it drew.
 */
export function deployMissionShips(context: MissionShipContext): readonly ShipSpec[] {
  const ships = deployShips({
    seed: context.seed,
    salt: `${context.id}:ships`,
    isWater: context.isWater,
    side: shipSideFor("entente", context.playerNation),
    alliance: "entente",
  });
  if (ships.length === 0 || !regionHasFrontLine(context.region)) return ships;
  const draw = new RngBank(createRngBankState(fnv1a32(`${context.id}:ensign`, context.seed >>> 0)));
  return ships.map((ship) => {
    // One draw a ship, in list order, so adding or removing a ship changes the
    // flags of the ships after it and of no other generator's anything.
    const alliance: ShipAlliance = draw.chance("mission", SHIP_CENTRAL_SHARE) ? "central" : "entente";
    return { ...ship, alliance, side: shipSideFor(alliance, context.playerNation) };
  });
}
